import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  ComposizioneArco,
  ComposizioneNodo,
  Composizione as DatiComposizione,
  ConfigurationItem,
  FiltriComposizione,
  ServiceHop,
} from '../api/types'
import { formatBytes } from './charts'
import { Card, EmptyState, ErrorBanner } from './common'
import Mappa, { type ArcoMappa, type GruppoNodo, type NodoMappa, type VoceLegenda } from './Mappa'
import { leggiLayout, scriviLayout, type LayoutMappa } from './mappaLayout'
import { t } from '../i18n/lingua'

/**
 * La composizione di un servizio: dall'ingresso, la catena delle chiamate e,
 * sotto, dove sta ogni pezzo.
 *
 * Risponde a «com'è fatto il servizio», che è una domanda diversa da «con chi
 * parla questo indirizzo» della mappa di correlazione: il balancer manda ai
 * web, i web chiamano l'app sulla 8443, l'app il database sulla 1433 — e tutti
 * stanno su quel cluster, su quel datastore, su quella rete. La stessa risposta
 * in due forme che si tengono per mano: la mappa (a fasce: client, ingresso, i
 * livelli della catena, poi rete, host, cluster, datastore, storage) e la
 * tabella «La catena», una riga per componente con chi lo chiama, cosa chiama e
 * dove sta.
 *
 * Usata dalla mappa di correlazione (partendo da un indirizzo) e dalla scheda di
 * un business service (partendo dai suoi ingressi): stessa vista, stessi
 * comandi, stessi salti a mano.
 */

const COLORE_TRAFFICO = '#3b82f6'
const COLORE_MANUALE = '#a855f7'
const STRATI: Record<string, { colore: string; tratteggio: string }> = {
  network: { colore: '#64748b', tratteggio: '1 4' },
  fisico: { colore: '#16a34a', tratteggio: '2 3' },
  storage: { colore: '#d97706', tratteggio: '6 4' },
}

const FASCE_INFRA = ['rete', 'host', 'cluster', 'datastore', 'storage'] as const

function nomeFasciaInfra(fascia: string): string {
  switch (fascia) {
    case 'rete':
      return t('Rete')
    case 'host':
      return t('Host')
    case 'cluster':
      return t('Cluster')
    case 'datastore':
      return t('Datastore')
    default:
      return t('Storage')
  }
}

function gruppoDi(n: ComposizioneNodo): GruppoNodo {
  if (n.kind === 'clients') return 'esterno'
  const tipo = (n.ci_type ?? '').toLowerCase()
  const classe = (n.ci_class ?? '').toLowerCase()
  if (!n.ci_id) return 'esterno'
  if (tipo === 'virtual_machine') return 'virtuale'
  if (tipo === 'virtual_cluster' || tipo === 'compute_cluster') return 'cluster'
  if (tipo === 'datastore' || tipo === 'storage_system') return 'storage'
  if (classe.includes('firewall')) return 'firewall'
  if (classe.includes('wireless')) return 'wireless'
  if (tipo === 'network_device' || tipo === 'port_group' || tipo === 'virtual_switch' || tipo === 'network_address')
    return 'rete'
  if (tipo.startsWith('cloud')) return 'cloud'
  return 'server'
}

function nomeDi(n: ComposizioneNodo | undefined): string {
  if (!n) return '—'
  if (n.kind === 'clients') return t('{n} client', { n: n.client_count ?? 0 })
  return n.name ?? n.label ?? n.address ?? n.id
}

function porteDi(a: ComposizioneArco): string {
  return a.ports.map((p) => (p.name ? `${p.name} (${p.port})` : String(p.port))).join(', ')
}

function spessorePer(bytes: number): number {
  if (!bytes || bytes <= 0) return 1.4
  return Math.min(5, Math.max(1.4, 1.2 + (Math.log10(bytes) - 3) * 0.55))
}

function eIndirizzo(testo: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(testo) || /^[0-9a-f:]+:[0-9a-f:]*$/i.test(testo)
}

interface Props {
  dati: DatiComposizione
  comuni: boolean
  onComuni: (valore: boolean) => void
  infra: boolean
  onInfra: (valore: boolean) => void
  /** I salti a mano già dichiarati, da elencare (e togliere). */
  salti: ServiceHop[]
  onAggiungiSalto?: (salto: ServiceHop) => void | Promise<void>
  onTogliSalto?: (indice: number) => void | Promise<void>
  /** Ciò che è stato tolto per pulire il risultato; senza `onFiltri` non si
   *  può togliere niente. */
  filtri?: FiltriComposizione
  onFiltri?: (filtri: FiltriComposizione) => void | Promise<void>
  altezza?: number
}

const NESSUN_FILTRO: FiltriComposizione = { nodes: [], edges: [], ports: [] }

export default function Composizione({
  dati,
  comuni,
  onComuni,
  infra,
  onInfra,
  salti,
  onAggiungiSalto,
  onTogliSalto,
  filtri = NESSUN_FILTRO,
  onFiltri,
  altezza,
}: Props) {
  const [layout, setLayout] = useState<LayoutMappa>(leggiLayout())
  const [scelto, setScelto] = useState<string | null>(null)
  // Il menu del tasto destro: su un nodo o su una chiamata, alle coordinate
  // dello schermo (il menu è `position: fixed`).
  const [menu, setMenu] = useState<{ x: number; y: number; nodo?: string; arco?: ComposizioneArco } | null>(null)

  useEffect(() => {
    if (!menu) return
    const chiudi = () => setMenu(null)
    const tasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null)
    }
    window.addEventListener('click', chiudi)
    window.addEventListener('keydown', tasto)
    return () => {
      window.removeEventListener('click', chiudi)
      window.removeEventListener('keydown', tasto)
    }
  }, [menu])

  // Pulire il risultato: togliere un oggetto (e ciò che pendeva solo da lui),
  // una chiamata, o una porta ovunque. L'ingresso no: una composizione «dietro
  // X» senza X non vuol dire niente.
  const puoTogliere = (id: string) => !!onFiltri && perIdKind(id) !== 'entry'
  const togliNodo = (id: string) => {
    if (!onFiltri || !puoTogliere(id) || filtri.nodes.includes(id)) return
    setScelto((s) => (s === id ? null : s))
    void onFiltri({ ...filtri, nodes: [...filtri.nodes, id] })
  }
  const togliArco = (a: ComposizioneArco) => {
    const chiave = `${a.src}>${a.dst}`
    if (!onFiltri || filtri.edges.includes(chiave)) return
    void onFiltri({ ...filtri, edges: [...filtri.edges, chiave] })
  }
  const togliPorta = (porta: number) => {
    if (!onFiltri || filtri.ports.includes(porta)) return
    void onFiltri({ ...filtri, ports: [...filtri.ports, porta] })
  }
  const rimetti = (tipo: 'nodes' | 'edges' | 'ports', valore: string | number) => {
    if (!onFiltri) return
    void onFiltri({ ...filtri, [tipo]: (filtri[tipo] as (string | number)[]).filter((v) => v !== valore) })
  }

  const perId = useMemo(() => new Map(dati.nodes.map((n) => [n.id, n])), [dati.nodes])
  function perIdKind(id: string) {
    return perId.get(id)?.kind
  }

  // Le fasce: client, ingresso, i livelli della catena, poi l'infrastruttura.
  const { livelloDi, nomeDelLivello } = useMemo(() => {
    const profondita = Math.max(1, ...dati.nodes.filter((n) => n.band === 'depth').map((n) => n.depth ?? 1))
    const base = 2 + profondita
    const livello = (n: ComposizioneNodo): number => {
      if (n.band === 'clients') return 0
      if (n.band === 'entry') return 1
      if (n.band === 'depth') return 1 + (n.depth ?? 1)
      return base + FASCE_INFRA.indexOf(n.band as (typeof FASCE_INFRA)[number])
    }
    const nomi = new Map<number, string>([
      [0, t('Client')],
      [1, t('Ingresso')],
    ])
    for (let d = 1; d <= profondita; d++) nomi.set(1 + d, t('Chiamate · livello {n}', { n: d }))
    FASCE_INFRA.forEach((f, i) => nomi.set(base + i, nomeFasciaInfra(f)))
    return { livelloDi: livello, nomeDelLivello: (l: number) => nomi.get(l) ?? '' }
  }, [dati.nodes])

  const nodiMappa = useMemo<NodoMappa[]>(
    () =>
      dati.nodes.map((n) => ({
        id: n.id,
        label: nomeDi(n),
        sotto: n.kind === 'clients' ? formatBytes(n.bytes ?? 0) : n.name ? n.address : n.ci_type,
        colore:
          n.kind === 'entry'
            ? 'var(--accent)'
            : n.kind === 'clients'
              ? 'var(--text-muted)'
              : n.kind === 'infra'
                ? 'var(--info)'
                : n.ci_id
                  ? 'var(--ok)'
                  : 'var(--warn)',
        gruppo: gruppoDi(n),
        livello: livelloDi(n),
        seme: n.kind === 'entry',
        segno: n.has_more ? '⁺' : n.manual ? '✎' : undefined,
      })),
    [dati.nodes, livelloDi],
  )

  const archiMappa = useMemo<ArcoMappa[]>(
    () =>
      dati.edges.map((a) => {
        const id = `${a.src}>${a.dst}|${a.kind}`
        const titolo = `${nomeDi(perId.get(a.src))} → ${nomeDi(perId.get(a.dst))}`
        if (a.kind === 'relation') {
          const strato = STRATI[a.layer ?? 'fisico'] ?? STRATI.fisico
          return { id, src: a.src, dst: a.dst, colore: strato.colore, tratteggio: strato.tratteggio, spessore: 1.3, titolo }
        }
        if (a.kind === 'manual') {
          return {
            id,
            src: a.src,
            dst: a.dst,
            colore: COLORE_MANUALE,
            tratteggio: '5 3',
            spessore: 2,
            etichetta: porteDi(a) || t('a mano'),
            titolo: `${titolo} · ${t('salto a mano')}${a.note ? ` · ${a.note}` : ''}`,
          }
        }
        return {
          id,
          src: a.src,
          dst: a.dst,
          colore: COLORE_TRAFFICO,
          spessore: spessorePer(a.bytes),
          etichetta: porteDi(a),
          titolo: `${titolo} · ${porteDi(a)} · ${formatBytes(a.bytes)}`,
        }
      }),
    [dati.edges, perId],
  )

  const arcoDi = useMemo(
    () => new Map(dati.edges.map((a) => [`${a.src}>${a.dst}|${a.kind}`, a])),
    [dati.edges],
  )

  const legenda: VoceLegenda[] = [
    { etichetta: t('Chiamata (spessore = volume)'), colore: COLORE_TRAFFICO },
    { etichetta: t('Salto a mano'), colore: COLORE_MANUALE, tratteggio: '5 3' },
    { etichetta: t('Gira su / membro di'), colore: STRATI.fisico.colore, tratteggio: STRATI.fisico.tratteggio },
    { etichetta: t('Archiviato su'), colore: STRATI.storage.colore, tratteggio: STRATI.storage.tratteggio },
    { etichetta: t('Collegato a (rete)'), colore: STRATI.network.colore, tratteggio: STRATI.network.tratteggio },
  ]

  // Per ogni componente: chi lo chiama, cosa chiama, e dove sta (seguendo le
  // relazioni: la VM sull'host, l'host nel cluster, il datastore sull'array).
  const catena = useMemo(() => {
    const uscenti = new Map<string, ComposizioneArco[]>()
    const entranti = new Map<string, ComposizioneArco[]>()
    for (const a of dati.edges) {
      uscenti.set(a.src, [...(uscenti.get(a.src) ?? []), a])
      entranti.set(a.dst, [...(entranti.get(a.dst) ?? []), a])
    }
    const infraDi = (id: string, fascia: string): ComposizioneNodo[] => {
      const trovati = new Map<string, ComposizioneNodo>()
      const visita = (da: string, passi: number) => {
        for (const a of uscenti.get(da) ?? []) {
          if (a.kind !== 'relation') continue
          const n = perId.get(a.dst)
          if (!n) continue
          if (n.band === fascia) trovati.set(n.id, n)
          else if (passi > 0 && n.kind === 'infra') visita(n.id, passi - 1)
        }
      }
      visita(id, 1)
      return [...trovati.values()]
    }
    return dati.nodes
      .filter((n) => n.kind === 'entry' || n.kind === 'component')
      .map((n) => ({
        nodo: n,
        chiamatoDa: (entranti.get(n.id) ?? []).filter((a) => a.kind !== 'relation'),
        chiama: (uscenti.get(n.id) ?? []).filter((a) => a.kind !== 'relation'),
        rete: infraDi(n.id, 'rete'),
        host: infraDi(n.id, 'host'),
        cluster: infraDi(n.id, 'cluster'),
        datastore: infraDi(n.id, 'datastore'),
        storage: infraDi(n.id, 'storage'),
      }))
  }, [dati.nodes, dati.edges, perId])

  // La tela cresce con le fasce: dieci fasce (client, ingresso, tre livelli,
  // cinque d'infrastruttura) in 620 pixel diventano pallini schiacciati con i
  // nomi troncati a tre lettere.
  const fasce = new Set(nodiMappa.map((n) => n.livello)).size
  const alta = altezza ?? Math.max(560, Math.min(1000, fasce * 92 + 90))

  const dettaglio = scelto ? perId.get(scelto) : undefined
  const rigaScelta = catena.find((r) => r.nodo.id === scelto)
  const componenti = catena.filter((r) => r.nodo.kind === 'component').length

  if (dati.nodes.length === 0) {
    return (
      <EmptyState
        title={t('Nessun ingresso riconosciuto')}
        hint={t('L’indirizzo di partenza non compare né in CMDB né nel traffico della finestra scelta.')}
      />
    )
  }

  const nomiLink = (nodi: ComposizioneNodo[]) =>
    nodi.length === 0
      ? '—'
      : nodi.map((n, i) => (
          <span key={n.id}>
            {i > 0 && ', '}
            {n.ci_id ? <Link to={`/cis/${n.ci_id}`}>{nomeDi(n)}</Link> : nomeDi(n)}
          </span>
        ))

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 16 }}>
        <label className="checkbox">
          <input type="checkbox" checked={infra} onChange={(e) => onInfra(e.target.checked)} />{' '}
          {t('Infrastruttura (host, cluster, datastore, rete)')}
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={comuni} onChange={(e) => onComuni(e.target.checked)} />{' '}
          {t('Servizi comuni (DNS, NTP, AD, monitoraggio, backup)')}
        </label>
        <span className="text-muted">
          {t('{n} componenti nella catena', { n: componenti })}
        </span>
      </div>
      {onFiltri && (
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Per pulire: tasto destro su un nodo o su una chiamata (anche «togli la porta ovunque»), Maiusc+clic su un nodo, o «Togli» nella tabella. Togliendo un oggetto sparisce anche ciò che si raggiungeva solo passando da lui.')}
        </p>
      )}

      {/* Ciò che la vista toglie si dice sopra la mappa, non sotto. */}
      {!comuni && dati.hidden.length > 0 && (
        <div className="banner banner--info">
          {t('Nascosti perché parlano con tutti:')}{' '}
          {dati.hidden.map((h) => `${h.category} (${h.calls})`).join(' · ')}.{' '}
          {t('Spunta «Servizi comuni» per vederli.')}
        </div>
      )}
      {dati.notes.map((nota) => (
        <div key={nota} className="banner banner--warn">
          {nota}
        </div>
      ))}
      {onFiltri && (filtri.nodes.length > 0 || filtri.edges.length > 0 || filtri.ports.length > 0) && (
        <p style={{ marginTop: 0 }}>
          {t('Tolti da te:')}{' '}
          {(dati.excluded?.nodes ?? filtri.nodes.map((id) => ({ id, name: id }))).map((n) => (
            <button
              key={n.id}
              type="button"
              className="chip"
              style={{ cursor: 'pointer' }}
              title={t('Rimetti')}
              onClick={() => rimetti('nodes', n.id)}
            >
              {n.name} ✕
            </button>
          ))}
          {(dati.excluded?.edges ?? filtri.edges.map((id) => ({ id, src_name: id, dst_name: '' }))).map((e) => (
            <button
              key={e.id}
              type="button"
              className="chip"
              style={{ cursor: 'pointer' }}
              title={t('Rimetti')}
              onClick={() => rimetti('edges', e.id)}
            >
              {e.src_name} → {e.dst_name} ✕
            </button>
          ))}
          {filtri.ports.map((p) => (
            <button
              key={p}
              type="button"
              className="chip"
              style={{ cursor: 'pointer' }}
              title={t('Rimetti')}
              onClick={() => rimetti('ports', p)}
            >
              {t('porta {n}', { n: p })} ✕
            </button>
          ))}{' '}
          <button type="button" className="linklike" onClick={() => void onFiltri(NESSUN_FILTRO)}>
            {t('Rimetti tutto')}
          </button>
        </p>
      )}
      {componenti === 0 && (
        <div className="banner banner--warn">
          {t('Dall’ingresso non esce nessuna chiamata nel traffico osservato. Se è il VIP di un bilanciatore che verso i server esce da indirizzi non inventariati (o non esporta flussi), dichiara il salto a mano qui sotto: da lì la catena riprende da sola.')}
        </div>
      )}

      <Mappa
        nodi={nodiMappa}
        archi={archiMappa}
        layout={layout}
        onLayout={(l) => {
          setLayout(l)
          scriviLayout(l)
        }}
        nomiFasce={nomeDelLivello}
        selezionato={scelto}
        onClickNodo={(id, e) => {
          // Maiusc+clic toglie il nodo: il gesto rapido per ripulire.
          if (e.shiftKey) togliNodo(id)
          else setScelto((s) => (s === id ? null : id))
        }}
        onMenuNodo={onFiltri ? (id, x, y) => setMenu({ x, y, nodo: id }) : undefined}
        onMenuArco={
          onFiltri
            ? (id, x, y) => {
                const arco = arcoDi.get(id)
                if (arco) setMenu({ x, y, arco })
              }
            : undefined
        }
        onClickSfondo={() => setScelto(null)}
        legenda={legenda}
        altezza={alta}
        etichetta={t('Composizione del servizio')}
      />

      {menu && (
        <div className="ctxmenu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
          {menu.nodo && (
            <>
              <button
                type="button"
                onClick={() => {
                  setScelto(menu.nodo ?? null)
                  setMenu(null)
                }}
              >
                {t('Dettaglio')}
              </button>
              {puoTogliere(menu.nodo) && (
                <button
                  type="button"
                  onClick={() => {
                    togliNodo(menu.nodo as string)
                    setMenu(null)
                  }}
                >
                  {t('Togli dalla composizione')}
                </button>
              )}
            </>
          )}
          {menu.arco &&
            (menu.arco.kind === 'relation' ? (
              puoTogliere(menu.arco.dst) && (
                <button
                  type="button"
                  onClick={() => {
                    togliNodo((menu.arco as ComposizioneArco).dst)
                    setMenu(null)
                  }}
                >
                  {t('Togli {nome}', { nome: nomeDi(perId.get(menu.arco.dst)) })}
                </button>
              )
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    togliArco(menu.arco as ComposizioneArco)
                    setMenu(null)
                  }}
                >
                  {t('Togli questa chiamata')}
                </button>
                {menu.arco.ports.slice(0, 4).map((p) => (
                  <button
                    key={p.port}
                    type="button"
                    onClick={() => {
                      togliPorta(p.port)
                      setMenu(null)
                    }}
                  >
                    {t('Togli la porta {porta} ovunque', { porta: p.name ? `${p.port} (${p.name})` : p.port })}
                  </button>
                ))}
              </>
            ))}
        </div>
      )}

      {dettaglio && (
        <Card
          title={nomeDi(dettaglio)}
          actions={
            puoTogliere(dettaglio.id) ? (
              <button type="button" className="secondary" onClick={() => togliNodo(dettaglio.id)}>
                {t('Togli dalla composizione')}
              </button>
            ) : undefined
          }
        >
          {dettaglio.kind === 'clients' ? (
            <>
              <p style={{ marginTop: 0 }}>
                {t('{n} client chiamano l’ingresso; i principali per volume:', { n: dettaglio.client_count ?? 0 })}
              </p>
              <ul>
                {(dettaglio.client_top ?? []).map((c) => (
                  <li key={c.id}>
                    {c.ci_id ? <Link to={`/cis/${c.ci_id}`}>{c.name ?? c.address}</Link> : c.address ?? c.id}
                    {' · '}
                    {c.ports.map((p) => p.name ?? p.port).join(', ')} · {formatBytes(c.bytes)}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                {dettaglio.ci_id ? (
                  <Link to={`/cis/${dettaglio.ci_id}`}>{t('Apri la scheda del CI →')}</Link>
                ) : (
                  <span className="text-muted">{t('Indirizzo non inventariato in CMDB.')}</span>
                )}{' '}
                {dettaglio.ci_type && <span className="chip">{dettaglio.ci_type}</span>}{' '}
                {(dettaglio.addresses ?? (dettaglio.address ? [dettaglio.address] : [])).map((a) => (
                  <span key={a} className="chip mono">
                    {a}
                  </span>
                ))}
              </p>
              {dettaglio.has_more && (
                <p className="field__hint">
                  {t('Chiama altri {n} oggetti oltre quelli mostrati (i più voluminosi sono in mappa).', {
                    n: dettaglio.omitted ?? 0,
                  })}
                </p>
              )}
              {(dettaglio.other_callers ?? 0) > 0 && (
                <p className="field__hint">
                  {t('Lo usano anche {n} oggetti fuori da questo servizio: se lo tocchi, se ne accorgono anche loro.', {
                    n: dettaglio.other_callers ?? 0,
                  })}
                </p>
              )}
              {rigaScelta && (
                <ul>
                  {rigaScelta.chiamatoDa.map((a, i) => (
                    <li key={`in-${i}`}>
                      ← {nomeDi(perId.get(a.src))} · {porteDi(a) || t('a mano')}
                      {a.kind === 'manual' && ` · ${t('salto a mano')}`}
                    </li>
                  ))}
                  {rigaScelta.chiama.map((a, i) => (
                    <li key={`out-${i}`}>
                      → {nomeDi(perId.get(a.dst))} · {porteDi(a) || t('a mano')}
                      {a.bytes > 0 && ` · ${formatBytes(a.bytes)}`}
                      {onFiltri && a.kind === 'traffic' && (
                        <>
                          {' '}
                          <button type="button" className="linklike" onClick={() => togliArco(a)}>
                            {t('togli la chiamata')}
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Card>
      )}

      <h3 className="guida__titolo" style={{ marginTop: 20 }}>
        {t('La catena del servizio')}
      </h3>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('Livello')}</th>
              <th>{t('Componente')}</th>
              <th>{t('Chiamato da')}</th>
              <th>{t('Chiama')}</th>
              {infra && (
                <>
                  <th>{t('Host')}</th>
                  <th>{t('Cluster')}</th>
                  <th>{t('Datastore')}</th>
                  <th>{t('Rete')}</th>
                </>
              )}
              {onFiltri && <th />}
            </tr>
          </thead>
          <tbody>
            {catena.map((r) => (
              <tr
                key={r.nodo.id}
                className={scelto === r.nodo.id ? 'is-selected' : undefined}
                style={{ cursor: 'pointer' }}
                onClick={() => setScelto(r.nodo.id)}
              >
                <td>{r.nodo.kind === 'entry' ? t('Ingresso') : `L${r.nodo.depth}`}</td>
                <td>
                  {r.nodo.ci_id ? <Link to={`/cis/${r.nodo.ci_id}`}>{nomeDi(r.nodo)}</Link> : nomeDi(r.nodo)}
                  {r.nodo.manual && <span className="chip">{t('a mano')}</span>}
                  <div className="text-muted mono" style={{ fontSize: 12 }}>
                    {r.nodo.address}
                  </div>
                </td>
                <td>
                  {r.chiamatoDa.map((a, i) => (
                    <div key={i}>
                      {nomeDi(perId.get(a.src))} <span className="mono">{porteDi(a)}</span>
                    </div>
                  ))}
                </td>
                <td>
                  {r.chiama.map((a, i) => (
                    <div key={i}>
                      {nomeDi(perId.get(a.dst))} <span className="mono">{porteDi(a)}</span>
                    </div>
                  ))}
                </td>
                {infra && (
                  <>
                    <td>{nomiLink(r.host)}</td>
                    <td>{nomiLink(r.cluster)}</td>
                    <td>
                      {nomiLink(r.datastore)}
                      {r.storage.length > 0 && <div className="text-muted">↳ {nomiLink(r.storage)}</div>}
                    </td>
                    <td>{nomiLink(r.rete)}</td>
                  </>
                )}
                {onFiltri && (
                  <td>
                    {puoTogliere(r.nodo.id) && (
                      <button
                        type="button"
                        className="secondary"
                        onClick={(e) => {
                          e.stopPropagation()
                          togliNodo(r.nodo.id)
                        }}
                      >
                        {t('Togli')}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(onAggiungiSalto || salti.length > 0) && (
        <SaltiAMano
          nodi={dati.nodes.filter((n) => n.kind === 'entry' || n.kind === 'component')}
          perId={perId}
          salti={salti}
          onAggiungi={onAggiungiSalto}
          onTogli={onTogliSalto}
        />
      )}
    </>
  )
}

/**
 * Il salto che il traffico non vede, dichiarato da chi conosce il servizio:
 * dal VIP ai server del pool, da un'applicazione a un sistema esterno.
 */
function SaltiAMano({
  nodi,
  perId,
  salti,
  onAggiungi,
  onTogli,
}: {
  nodi: ComposizioneNodo[]
  perId: Map<string, ComposizioneNodo>
  salti: ServiceHop[]
  onAggiungi?: (salto: ServiceHop) => void | Promise<void>
  onTogli?: (indice: number) => void | Promise<void>
}) {
  const [da, setDa] = useState(nodi[0]?.id ?? '')
  const [verso, setVerso] = useState('')
  const [scelta, setScelta] = useState<ConfigurationItem | null>(null)
  const [porta, setPorta] = useState('')
  const [nota, setNota] = useState('')
  const [trovati, setTrovati] = useState<ConfigurationItem[]>([])
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!da && nodi[0]) setDa(nodi[0].id)
  }, [nodi, da])

  // La ricerca del CI di destinazione: nome, indirizzo, MAC — la stessa della
  // lente. Un indirizzo scritto per intero vale anche senza CI.
  useEffect(() => {
    const testo = verso.trim()
    if (scelta || testo.length < 2) {
      setTrovati([])
      return
    }
    const timer = window.setTimeout(() => {
      api
        .cis({ q: testo, limit: 8 })
        .then(setTrovati)
        .catch(() => setTrovati([]))
    }, 250)
    return () => window.clearTimeout(timer)
  }, [verso, scelta])

  const nomeEstremo = (ci: string | null, addr: string | null, nome?: string | null) =>
    nome ?? (ci ? nomeDi(perId.get(`ci:${ci}`)) : null) ?? addr ?? '—'

  async function aggiungi() {
    if (!onAggiungi) return
    const partenza = perId.get(da)
    if (!partenza) return
    const testo = verso.trim()
    if (!scelta && !eIndirizzo(testo)) {
      setErrore(new Error(t('Scegli un oggetto dall’elenco o scrivi un indirizzo IP.')))
      return
    }
    setBusy(true)
    setErrore(null)
    try {
      await onAggiungi({
        from_ci_id: partenza.ci_id,
        from_address: partenza.ci_id ? null : partenza.address,
        from_name: partenza.name,
        to_ci_id: scelta?.id ?? null,
        to_address: scelta ? null : testo,
        to_name: scelta?.name ?? null,
        port: porta.trim() ? Number(porta) : null,
        note: nota.trim() || null,
      })
      setVerso('')
      setScelta(null)
      setPorta('')
      setNota('')
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h3 className="guida__titolo" style={{ marginTop: 20 }}>
        {t('Salti a mano')}
      </h3>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Il salto che il traffico non vede — il VIP verso i server del pool, un’applicazione verso un sistema che non esporta flussi — si dichiara qui. La catena lo segue come un salto osservato e da lì riprende a seguire il traffico.')}
      </p>
      {salti.length > 0 && (
        <ul>
          {salti.map((s, i) => (
            <li key={s.id ?? i}>
              {nomeEstremo(s.from_ci_id, s.from_address, s.from_name)} →{' '}
              {nomeEstremo(s.to_ci_id, s.to_address, s.to_name)}
              {s.port ? ` · ${s.port}` : ''}
              {s.note ? ` · ${s.note}` : ''}{' '}
              {onTogli && (
                <button type="button" className="linklike" onClick={() => onTogli(i)}>
                  {t('Togli')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {onAggiungi && (
        <>
          <ErrorBanner error={errore} />
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ maxWidth: 260 }}>
              <label htmlFor="salto-da">{t('Da')}</label>
              <select id="salto-da" value={da} onChange={(e) => setDa(e.target.value)}>
                {nodi.map((n) => (
                  <option key={n.id} value={n.id}>
                    {nomeDi(n)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ position: 'relative' }}>
              <label htmlFor="salto-verso">{t('Verso (oggetto o indirizzo IP)')}</label>
              <input
                id="salto-verso"
                type="text"
                value={scelta ? scelta.name ?? scelta.primary_key : verso}
                placeholder={t('es. web-01 oppure 10.0.2.11')}
                onChange={(e) => {
                  setScelta(null)
                  setVerso(e.target.value)
                }}
              />
              {trovati.length > 0 && (
                <div className="ctxmenu" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 5 }}>
                  {trovati.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setScelta(c)
                        setTrovati([])
                      }}
                    >
                      {c.name ?? c.primary_key}
                      {c.attributes?.mgmt_ip ? ` · ${String(c.attributes.mgmt_ip)}` : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="field" style={{ maxWidth: 110 }}>
              <label htmlFor="salto-porta">{t('Porta')}</label>
              <input
                id="salto-porta"
                type="number"
                min={1}
                max={65535}
                value={porta}
                onChange={(e) => setPorta(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="salto-nota">{t('Nota')}</label>
              <input
                id="salto-nota"
                type="text"
                value={nota}
                placeholder={t('es. pool web del VIP')}
                onChange={(e) => setNota(e.target.value)}
              />
            </div>
            <button type="button" onClick={aggiungi} disabled={busy || !da || (!scelta && !verso.trim())}>
              {t('Aggiungi il salto')}
            </button>
          </div>
        </>
      )}
    </>
  )
}
